import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Table,
  TableColumnType,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import {
  ICreateShipmentInfo,
  IParamFilterShipment,
} from "../../models/Tracking";
import { useCreateShipmentInfo } from "../../query/Tracking";
import { notify } from "../../utils/toast";

interface ModalAddShipmentInfoProps {
  isOpen: boolean;
  onClose: () => void;
  params: IParamFilterShipment;
}

const ModalAddShipmentInfo = ({
  isOpen,
  onClose,
  params,
}: ModalAddShipmentInfoProps) => {
  const [form] = Form.useForm();
  const [shipments, setShipments] = useState<ICreateShipmentInfo[]>([]);
  const createMutation = useCreateShipmentInfo(params);
  const handleAddShipment = async () => {
    try {
      const values = await form.validateFields();
      const newShipment: ICreateShipmentInfo = {
        ...values,
        ata: values.ata ? dayjs(values.ata).format("YYYY-MM-DD") : "",
        declarationStatus: false,
        deliveryStatus: false,
      };
      setShipments([...shipments, newShipment]);
      form.resetFields();
    } catch (error) {}
  };

  const handleDeleteShipment = (hawbHbl: string) => {
    setShipments(shipments.filter((s) => s.hawbHbl !== hawbHbl));
  };

  const handleSaveShipments = async () => {
    try {
      await createMutation.mutateAsync(shipments);
      setShipments([]);
      notify.success("Thêm vận đơn thành công");
      onClose();
    } catch (error) {
      notify.error("Thêm vận đơn thất bại");
    }
  };

  const columns: TableColumnType<ICreateShipmentInfo>[] = [
    {
      title: "HAWB/HBL",
      dataIndex: "hawbHbl",
      key: "hawbHbl",
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Hãng vận chuyển",
      dataIndex: "carrierForwarder",
      key: "carrierForwarder",
      width: 120,
    },
    {
      title: "Loại vận đơn",
      dataIndex: "shipmentType",
      key: "shipmentType",
      width: 100,
    },
    {
      title: "Người bán",
      dataIndex: "seller",
      key: "seller",
      width: 100,
    },
    {
      title: "ATA",
      dataIndex: "ata",
      key: "ata",
      width: 120,
      render: (text) =>
        text ? (
          <Tag color="green">{dayjs(text).format("DD/MM/YYYY")}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Người phụ trách",
      dataIndex: "cdsPic",
      key: "cdsPic",
      width: 100,
    },
    {
      title: "Hành động",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Xóa vận đơn này?"
          onConfirm={() => handleDeleteShipment(record.hawbHbl)}
          okText="Xóa"
          cancelText="Hủy"
        >
          <Button type="primary" danger size="small">
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="Thêm thông tin vận đơn"
      open={isOpen}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Đóng
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createMutation.isPending}
          onClick={handleSaveShipments}
        >
          Lưu
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16 }}>Nhập thông tin vận đơn</h3>
        <Form form={form} layout="vertical" size="small">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
            }}
          >
            <Form.Item
              label="HAWB/HBL"
              name="hawbHbl"
              rules={[{ required: true, message: "Vui lòng nhập HAWB/HBL" }]}
            >
              <Input placeholder="VD: DHL123456" />
            </Form.Item>
            <Form.Item
              label="Hãng vận chuyển"
              name="carrierForwarder"
              rules={[
                { required: true, message: "Vui lòng nhập hãng vận chuyển" },
              ]}
            >
              <Input placeholder="VD: DHL, FedEx" />
            </Form.Item>
            <Form.Item
              label="Loại vận đơn"
              name="shipmentType"
              rules={[
                { required: true, message: "Vui lòng nhập loại vận đơn" },
              ]}
            >
              <Input placeholder="VD: Express, Standard" />
            </Form.Item>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
            }}
          >
            <Form.Item label="Người bán" name="seller">
              <Input placeholder="Tên người bán" />
            </Form.Item>
            <Form.Item
              label="ATA"
              name="ata"
              rules={[{ required: true, message: "Vui lòng chọn ngày ATA" }]}
            >
              <DatePicker
                placeholder="Chọn ngày ATA"
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="CDS-PIC" name="cdsPic">
              <Input placeholder="Người phụ trách" />
            </Form.Item>
          </div>
          <Form.Item label="Ghi chú" name="notes">
            <Input.TextArea placeholder="Ghi chú thêm" rows={2} />
          </Form.Item>
          <Button type="primary" onClick={handleAddShipment}>
            Thêm vào danh sách
          </Button>
        </Form>
      </div>

      {/* Bảng preview */}
      <Divider />
      <div>
        <h3 style={{ marginBottom: 16 }}>
          Danh sách vận đơn ({shipments.length})
        </h3>
        <Table
          columns={columns}
          dataSource={shipments}
          rowKey={(record) => record.hawbHbl}
          pagination={false}
          size="small"
          scroll={{ x: 1000 }}
        />
      </div>
    </Modal>
  );
};

export default ModalAddShipmentInfo;
